import { useMemo } from "react";
import { format } from "date-fns";
import { Download, Link2, Pencil, Trash2, Upload } from "lucide-react";

import { ArquivoLogDTO } from "@/types/arquivo.type";
import {
  ArquivoLogAcao,
  ArquivoLogAcaoMap,
  ArquivoLogFiltrosCriteria,
  ArquivoLogFiltrosOptions,
  TipoArquivoMap,
} from "@workflow/types";

import { Column, DataTable } from "@/components/table/DataTable";
import { useServerDataTable } from "@/hooks/data-table/use-server-data-table";
import {
  arquivosAdminQueries,
  arquivosAdminKeys,
} from "@/services/api/queries/arquivos-admin.queries";

import { Person } from "@/components/shared/person";
import { cn } from "@/lib/utils";
import { ArquivoNomeCell, TipoBadge } from "./arquivo-visual";

/** Ícone + cor por ação de log. */
const ACAO_CONFIG: Record<ArquivoLogAcao, { icon: typeof Download; className: string }> = {
  [ArquivoLogAcao.DOWNLOAD]: {
    icon: Download,
    className: "border-emerald-500/20 bg-emerald-500/[0.07] text-emerald-700 dark:text-emerald-300",
  },
  [ArquivoLogAcao.UPLOAD]: {
    icon: Upload,
    className: "border-blue-500/20 bg-blue-500/[0.07] text-blue-700 dark:text-blue-300",
  },
  [ArquivoLogAcao.REPLACE]: {
    icon: Pencil,
    className: "border-amber-500/20 bg-amber-500/[0.07] text-amber-700 dark:text-amber-300",
  },
  [ArquivoLogAcao.DELETE]: {
    icon: Trash2,
    className: "border-red-500/20 bg-red-500/[0.07] text-red-700 dark:text-red-300",
  },
  [ArquivoLogAcao.GET_URL]: {
    icon: Link2,
    className: "border-border/60 bg-muted text-muted-foreground",
  },
};

const AcaoCell = ({ acao }: { acao: ArquivoLogAcao }) => {
  const config = ACAO_CONFIG[acao];
  const Icon = config?.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border py-1 pl-1.5 pr-2 text-[11px] font-medium",
        config?.className ?? "border-border/60 bg-muted text-muted-foreground",
      )}
    >
      {Icon && <Icon className="h-3 w-3 shrink-0 opacity-70" />}
      {ArquivoLogAcaoMap[acao] || acao}
    </span>
  );
};

export function ArquivoLogListTable() {
  const { tableProps, isError, invalidate } = useServerDataTable<
    ArquivoLogDTO,
    ArquivoLogFiltrosOptions,
    ArquivoLogFiltrosCriteria
  >({
    queryOptions: arquivosAdminQueries.logsList,
    filters: arquivosAdminQueries.logsFiltros,
    invalidateKey: arquivosAdminKeys.logsList,
  });

  const columns = useMemo<Column<ArquivoLogDTO>[]>(
    () => [
      {
        key: "arquivo.id",
        title: "ID do Arquivo",
        sortable: true,
        width: "min-w-[80px]",
        render: (log) =>
          log.arquivo ? (
            <span className="font-mono text-xs text-muted-foreground">{log.arquivo.id}</span>
          ) : (
            <span className="text-xs italic text-muted-foreground/70">—</span>
          ),
        raw: (log) => log.arquivo?.id?.toString() || "",
      },
      {
        key: "arquivo",
        title: "Arquivo",
        width: "w-[240px] max-w-[240px]",
        render: (log) =>
          log.arquivo ? (
            <ArquivoNomeCell nome={log.arquivo.nome} extensao={log.arquivo.extensao} />
          ) : (
            <span className="text-xs italic text-muted-foreground/70">Arquivo removido</span>
          ),
        raw: (log) => log.arquivo?.nome || "",
      },
      {
        key: "tipo",
        title: "Tipo",
        width: "min-w-[160px]",
        render: (log) =>
          log.arquivo ? (
            <TipoBadge tipo={log.arquivo.tipo} />
          ) : (
            <span className="text-xs italic text-muted-foreground/70">—</span>
          ),
        raw: (log) => (log.arquivo ? TipoArquivoMap[log.arquivo.tipo] || log.arquivo.tipo : ""),
      },
      {
        key: "arquivoLog.acao",
        title: "Ação",
        sortable: true,
        width: "min-w-[140px]",
        render: (log) => <AcaoCell acao={log.acao} />,
        raw: (log) => ArquivoLogAcaoMap[log.acao] || log.acao,
      },
      {
        key: "realizadoPor",
        title: "Realizado por",
        width: "min-w-[220px]",
        render: (log) =>
          log.realizadoPor ? (
            <Person.Root pessoa={log.realizadoPor} asClickable className="flex items-center gap-2">
              <Person.Avatar />
              <Person.Name className="text-sm" />
            </Person.Root>
          ) : (
            <span className="text-xs italic text-muted-foreground/70">—</span>
          ),
        raw: (log) => log.realizadoPor?.nome || "",
      },
      {
        key: "arquivoLog.criadoEm",
        title: "Data",
        sortable: true,
        width: "min-w-[160px]",
        render: (log) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {log.criadoEm ? format(new Date(log.criadoEm), "dd/MM/yyyy HH:mm") : "—"}
          </span>
        ),
        raw: (log) => (log.criadoEm ? new Date(log.criadoEm).toISOString() : ""),
      },
    ],
    [],
  );

  return (
    <DataTable
      {...tableProps}
      storageKey="arquivo-logs-admin-v1"
      columns={columns}
      defaultSortColumn="arquivoLog.criadoEm"
      defaultSortOrder="DESC"
      searchPlaceholder="Pesquisar por ID do arquivo, nome ou pessoa..."
      emptyMessage={
        isError
          ? "Não foi possível carregar o histórico. Tente recarregar."
          : "Nenhum registro encontrado."
      }
      isError={isError}
      onRetry={invalidate}
      exportFilename="historico-arquivos"
    />
  );
}
